import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  ArrowUpRight,
  Briefcase,
  CircleEllipsis,
  IndianRupee,
  LayoutList,
  LogOut,
  MessagesSquare,
  Settings,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useAuth, useStaffSession } from '@/lib/auth';
import { useWaitingCounts } from '@/lib/leads';
import { Mark, Wordmark } from './Logo';

/**
 * The frame every Desk screen sits in.
 *
 * Desktop: an ink rail on the left, the one block of ink on the screen, with
 * the paper work surface beside it. Phone: an ink tab bar at the thumb with the
 * four places used on the move, and everything else behind "More". The current
 * place is marked with the acid square that ends every heading on bitbew.com.
 */

interface Place {
  to: '/' | '/enquiries' | '/clients' | '/work' | '/money' | '/settings';
  label: string;
  icon: LucideIcon;
  /** Shown in the phone tab bar; the rest live under More. */
  thumb?: boolean;
}

const PLACES: Place[] = [
  { to: '/', label: 'Today', icon: LayoutList, thumb: true },
  { to: '/enquiries', label: 'Enquiries', icon: MessagesSquare, thumb: true },
  { to: '/clients', label: 'Clients', icon: Users, thumb: true },
  { to: '/work', label: 'Work', icon: Briefcase, thumb: true },
  { to: '/money', label: 'Money', icon: IndianRupee },
  { to: '/settings', label: 'Settings', icon: Settings },
];

function useIsActive() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  return (to: string) => (to === '/' ? path === '/' : path === to || path.startsWith(`${to}/`));
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[15rem_1fr]">
      <Rail />
      <div className="min-w-0 pb-[calc(4.25rem+env(safe-area-inset-bottom))] lg:pb-0">{children}</div>
      <TabBar />
    </div>
  );
}

/* ------------------------------------------------------------- desktop rail */

function Rail() {
  const active = useIsActive();
  const { signOut } = useAuth();
  const session = useStaffSession();
  const counts = useWaitingCounts();

  return (
    <aside
      data-field="ink"
      className="sticky top-0 hidden h-dvh flex-col bg-bg px-3 py-5 text-fg lg:flex"
      aria-label="Desk"
    >
      <Link to="/" className="flex items-center gap-2.5 rounded-md px-3 py-1" aria-label="Desk home">
        <Mark className="size-6" />
        <Wordmark className="h-3" />
        <span className="ml-auto text-xs font-semibold text-fg-dim">Desk</span>
      </Link>

      <nav className="mt-8 grid gap-0.5" aria-label="Main">
        {PLACES.map((p) => {
          const on = active(p.to);
          const count = p.to === '/enquiries' ? counts.data?.newLeads : undefined;
          return (
            <Link
              key={p.to}
              to={p.to}
              aria-current={on ? 'page' : undefined}
              className={`group flex h-10 items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors duration-150 ${
                on ? 'bg-bg-raised text-fg' : 'text-fg-dim hover:bg-bg-raised/60 hover:text-fg'
              }`}
            >
              <p.icon className="size-[1.125rem] shrink-0" aria-hidden strokeWidth={1.75} />
              <span>{p.label}</span>
              {on && <span className="size-1.5 bg-acid" aria-hidden />}
              {count ? (
                <span className="count ml-auto" aria-label={`${count} new`}>
                  {count}
                </span>
              ) : null}
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto grid gap-1 border-t border-edge pt-4">
        <a
          href="https://bitbew.com"
          target="_blank"
          rel="noreferrer"
          className="flex h-9 items-center gap-3 rounded-md px-3 text-sm text-fg-dim hover:bg-bg-raised/60 hover:text-fg"
        >
          <ArrowUpRight className="size-4" aria-hidden />
          bitbew.com
        </a>
        <button
          type="button"
          onClick={signOut}
          className="flex h-9 items-center gap-3 rounded-md px-3 text-left text-sm text-fg-dim hover:bg-bg-raised/60 hover:text-fg"
        >
          <LogOut className="size-4" aria-hidden />
          <span className="min-w-0 truncate">Sign out</span>
        </button>
        <p className="truncate px-3 pt-1 text-2xs text-fg-faint" title={session.user.email}>
          {session.user.email}
        </p>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------ phone tab bar */

function TabBar() {
  const active = useIsActive();
  const counts = useWaitingCounts();
  const moreOn = active('/money') || active('/settings') || active('/more');

  return (
    <nav
      data-field="ink"
      aria-label="Main"
      className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-5 border-t border-edge bg-bg pb-[env(safe-area-inset-bottom)] text-fg lg:hidden"
    >
      {PLACES.filter((p) => p.thumb).map((p) => {
        const on = active(p.to);
        const count = p.to === '/enquiries' ? counts.data?.newLeads : undefined;
        return (
          <Link
            key={p.to}
            to={p.to}
            aria-current={on ? 'page' : undefined}
            className={`relative flex h-[4.25rem] flex-col items-center justify-center gap-1 text-2xs font-semibold ${
              on ? 'text-fg' : 'text-fg-dim'
            }`}
          >
            <span className="relative">
              <p.icon className="size-5" aria-hidden strokeWidth={on ? 2 : 1.75} />
              {count ? (
                <span className="count absolute -top-1.5 left-3.5" aria-label={`${count} new`}>
                  {count}
                </span>
              ) : null}
            </span>
            <span className="flex items-center gap-1">
              {p.label}
              {on && <span className="size-1 bg-acid" aria-hidden />}
            </span>
          </Link>
        );
      })}
      <Link
        to="/more"
        aria-current={moreOn ? 'page' : undefined}
        className={`flex h-[4.25rem] flex-col items-center justify-center gap-1 text-2xs font-semibold ${
          moreOn ? 'text-fg' : 'text-fg-dim'
        }`}
      >
        <CircleEllipsis className="size-5" aria-hidden strokeWidth={1.75} />
        <span className="flex items-center gap-1">
          More
          {moreOn && <span className="size-1 bg-acid" aria-hidden />}
        </span>
      </Link>
    </nav>
  );
}

/** Phone-only list of the places that do not fit in the tab bar. */
export function MoreList() {
  const { signOut } = useAuth();
  const session = useStaffSession();
  return (
    <ul className="divide-y divide-edge rounded-lg border border-edge bg-bg-raised">
      {PLACES.filter((p) => !p.thumb).map((p) => (
        <li key={p.to}>
          <Link to={p.to} className="flex h-14 items-center gap-3 px-4 font-semibold">
            <p.icon className="size-5 text-fg-dim" aria-hidden strokeWidth={1.75} />
            {p.label}
          </Link>
        </li>
      ))}
      <li>
        <a href="https://bitbew.com" target="_blank" rel="noreferrer" className="flex h-14 items-center gap-3 px-4">
          <ArrowUpRight className="size-5 text-fg-dim" aria-hidden />
          bitbew.com
        </a>
      </li>
      <li>
        <button type="button" onClick={signOut} className="flex h-14 w-full items-center gap-3 px-4 text-left">
          <LogOut className="size-5 text-fg-dim" aria-hidden />
          <span>
            Sign out <span className="text-sm text-fg-dim">· {session.user.email}</span>
          </span>
        </button>
      </li>
    </ul>
  );
}

/** The top of every screen: the title, and the screen's own actions at the right. */
export function PageHeader({ title, children, back }: { title: string; children?: ReactNode; back?: ReactNode }) {
  return (
    <header className="sticky top-0 z-20 border-b border-edge bg-paper">
      <div className="mx-auto flex min-h-16 max-w-6xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        {back}
        {!back && <Mark className="size-5 text-ink lg:hidden" />}
        <h1 className="page-title min-w-0 flex-1 truncate !text-xl sm:!text-2xl">{title}</h1>
        {children && <div className="flex shrink-0 items-center gap-2">{children}</div>}
      </div>
    </header>
  );
}

export function Page({ children }: { children: ReactNode }) {
  return <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>;
}
